import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { Box, Stack, Typography } from '@mui/material'
import { getDateTimeStringSafe, getTimeDurationStringFromISO8601 } from 'utils/time/timeUtils'

import { CopyButton } from 'components/common/CopyButton'
import { CreativeInstructionsDTO } from 'models/creativeInstructions'
import { FC, useEffect, useState } from 'react'
import { ProductInstructionLine } from '../ProductInstructionLine'
import { additionalComments, trimLikeApp } from './additionalComments'
import { deriveCreativeInstructionsProductType } from 'utils/product'
import { logAnalyticsEvent } from 'utils/analytics/analyticsUtils'
import { logger } from 'utils/logging'
import { stripSegmentMarkup } from 'utils/helpers/stringUtils'
import { useTranslation } from 'react-i18next'

interface Props {
  data: CreativeInstructionsDTO
}

/**
 * Non-null `String` fields of the full BE DTO that the public `application/json` variant omits.
 * The endpoint is content-negotiated on one path, so a vendor mimetype that stops matching
 * (e.g. after a version bump) yields a 200 with the public subset instead of an error.
 */
const FULL_VARIANT_KEYS = ['shootingAddress', 'organizationGeneralCommentsForCreative', 'meetingInstructions', 'shootingInstructions'] as const
// `assignmentGeneralCommentsForCreative` is left out on purpose while a backend from before backend-platform #664
// can answer (it omits the field, and this check would warn on every render). Once #664 is live everywhere, make
// the field required in the DTO and add it here; until then the `absent` outcome below is the signal.

/** Which client text a creative copied, sent instead of the text itself. Same values as the creatives app. */
type CopiedInstructionsKind = 'shooting' | 'meeting' | 'clientComments' | 'additionalComments'

/** Why the "Additional comments" field shows or stays hidden; sent instead of the text. */
type AdditionalCommentsOutcome = 'absent' | 'empty' | 'covered' | 'shown'

/**
 * Orange instructions banner shown above the guidelines list when the page is
 * opened from the "Follow instructions" button. Renders the per-assignment
 * dynamic data returned by `GET /creative-instructions/{pairingId}`.
 */
export const InstructionsBanner: FC<Props> = ({ data }) => {
  const { t } = useTranslation(['creative_guidelines', 'product_kind', 'product_type', 'segment', 'category', 'language'])

  // The banner writes the clipboard itself rather than through CopyButton, which neither awaits nor
  // reports the write: the event must count real copies, and a refused write has to reach Sentry and
  // the CT. Resolves to whether the text is on the clipboard.
  const copyToClipboard = (kind: CopiedInstructionsKind, text: string): Promise<boolean> =>
    // `navigator.clipboard` is undefined outside a secure context; Promise.resolve turns that into a rejection.
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(() => {
        logAnalyticsEvent(AnalyticsEvent.CREATIVE_INSTRUCTIONS_COPIED, { kind, assignmentId: data.assignmentId, surface: 'webGuidelines' })
        return true
      })
      .catch((error: unknown) => {
        // Name and message only: a DOMException is not `instanceof Error` everywhere and serializes to {}.
        const { name, message } = (error ?? {}) as { name?: unknown, message?: unknown }
        const reason = { name: typeof name === 'string' ? name : typeof error, message: typeof message === 'string' ? message : undefined }
        // The cause goes in the message: Sentry groups and searches messages, not the attached context.
        logger.warn(`Copying the creative instructions failed: ${reason.name}`, reason, { context: { component: 'InstructionsBanner', action: 'copyInstructions', metadata: { assignmentId: data.assignmentId, kind } } })
        return false
      })

  const additionalCommentsText = additionalComments(data.assignmentGeneralCommentsForCreative, [data.organizationGeneralCommentsForCreative, data.shootingInstructions, data.meetingInstructions])
  const additionalCommentsOutcome: AdditionalCommentsOutcome = !('assignmentGeneralCommentsForCreative' in data)
    ? 'absent'
    : !trimLikeApp(data.assignmentGeneralCommentsForCreative ?? '')
      ? 'empty'
      : additionalCommentsText ? 'shown' : 'covered'

  // The only signal that the backend sends the field (`absent` until it does) and how often the app's rule
  // hides it (`covered`), mirroring the app's `additionalComments` count. Once per assignment and outcome.
  useEffect(() => {
    logAnalyticsEvent(AnalyticsEvent.CREATIVE_INSTRUCTIONS_ADDITIONAL_COMMENTS, { outcome: additionalCommentsOutcome, assignmentId: data.assignmentId, surface: 'webGuidelines' })
  }, [data.assignmentId, additionalCommentsOutcome])

  const productType = data.classificationFlags
    ? deriveCreativeInstructionsProductType(data.classificationFlags, data.documentSubtype)
    : null

  const keyPickupDateTime = data.keyPickupDetails?.dateTime
  const keyPickupTime = getDateTimeStringSafe(keyPickupDateTime, { timezone: data.timezone })

  // The backend nulls classificationFlags together with productKind when the assignment has no
  // primary product. Flags missing while the kind is present can only be bad data (a rolled-back
  // backend, a serialization regression), which would otherwise render exactly like the legitimate
  // primary-less case — log it so it surfaces in Sentry instead of as a CT support ticket.
  useEffect(() => {
    if (!data.classificationFlags && data.productKind) {
      logger.warn('Creative instructions carry a product kind but no classification flags', { assignmentId: data.assignmentId, productKind: data.productKind }, { context: { component: 'InstructionsBanner' } })
    }
  }, [data.assignmentId, data.classificationFlags, data.productKind])

  // Every PII field is hidden when absent, so the public variant would render exactly like the
  // old banner and ISSUE-3655 would come back with no signal. Log key names only, never values.
  useEffect(() => {
    const missingKeys = FULL_VARIANT_KEYS.filter(key => !(key in data))
    if (missingKeys.length) {
      logger.warn('Creative instructions arrived without the full-variant fields', undefined, { context: { component: 'InstructionsBanner', action: 'detectPublicVariant', metadata: { assignmentId: data.assignmentId, missingKeys } } })
    }
  }, [data])

  // `getDateTimeStringSafe` returns null both for a missing time and for one it cannot parse; the
  // second case would leave the key pickup block looking complete with no time and no trace.
  useEffect(() => {
    if (keyPickupDateTime && !keyPickupTime) {
      logger.warn('Key pickup time could not be formatted', undefined, { context: { component: 'InstructionsBanner', action: 'formatKeyPickupTime', metadata: { assignmentId: data.assignmentId, dateTime: keyPickupDateTime } } })
    }
  }, [data.assignmentId, keyPickupDateTime, keyPickupTime])

  return (
    <Box
      // Client PII (contact, address, briefing) must not reach session replay: see utils/analytics/fullstory.ts.
      className="fs-mask"
      sx={{
        backgroundColor: '#FFF4E5',
        borderLeft: '0.4rem solid #ED6C02',
        borderRadius: '0.4rem',
        padding: '2rem 2.4rem',
      }}
    >
      <Stack gap="1.6rem">

        <Typography variant="text-lg" fontWeight={700} color="#B45309">
          {t('instructions_banner_heading')}
        </Typography>

        {/* No `generatedCreativeInstructions` (ISSUE-4076): the AI summary sometimes came back unreadable,
            e.g. Georgian letters in a German briefing. The CT reads the client's own texts it is built from,
            each with a copy button so they can paste it into a translator, like in the creatives app. */}
        <Field label={t('shooting_instructions')} value={data.shootingInstructions} isMultiline onCopy={text => copyToClipboard('shooting', text)} />

        <Field label={t('meeting_instructions')} value={data.meetingInstructions} isMultiline onCopy={text => copyToClipboard('meeting', text)} />

        <Field label={t('general_comments')} value={data.organizationGeneralCommentsForCreative} isMultiline onCopy={text => copyToClipboard('clientComments', text)} />

        {/* The assignment's own comments, only when they say more than the three texts above: same card and
            same rule as the creatives app's "Additional comments" (see `additionalComments`). */}
        <Field
          label={t('additional_comments')}
          value={additionalCommentsText}
          isMultiline
          onCopy={text => copyToClipboard('additionalComments', text)}
        />

        {/* `assignmentId` is the internal UUID; the CT only knows the public `{orderId}-{letter}` id,
            which this DTO does not carry, so the field is hidden until the BE exposes it. */}
        <Grid>

          <Field label={t('address')} value={data.shootingAddress} />

          <Field label={t('start_date_time')} value={getDateTimeStringSafe(data.shootingStartDateTime, { timezone: data.timezone })} />

          <Field label={t('duration')} value={getTimeDurationStringFromISO8601(data.shootingDuration)} />

          <Field label={t('timezone')} value={data.timezone} />

          <Field label={t('quantity')} value={data.productQuantity} />

          <Field
            label={t('upload_count')}
            value={data.ctMinUploadCount != null && data.ctMaxUploadCount != null ? `${data.ctMinUploadCount}–${data.ctMaxUploadCount}` : null}
          />

          <Field label={t('product_kind')} value={data.productKind ? t(`product_kind:${data.productKind}`) : null} />

          <Field label={t('product_type')} value={productType ? t(`product_type:${productType}`) : null} />

          <Field label={t('product_category')} value={data.productCategory ? t(`category:${data.productCategory}`) : null} />

          <Field label={t('product_segment')} value={data.productSegment ? stripSegmentMarkup(t(`segment:${data.productSegment}`)) : null} />

          <Field label={t('customer_language')} value={data.customerLanguage ? t(`language:${data.customerLanguage}`) : null} />

          <Field label={t('creative_language')} value={data.creativeLanguage ? t(`language:${data.creativeLanguage}`) : null} />

        </Grid>

        <Grid>

          <Field label={t('contact_name')} value={data.contactPersonName} />

          <Field label={t('contact_phone')} value={data.contactPersonPhone} linkType="tel" />

          <Field label={t('contact_email')} value={data.contactPersonEmail} linkType="mailto" />

          <Field label={t('customer_name')} value={data.customerName} />

          <Field label={t('organization_name')} value={data.organizationName} />

        </Grid>

        {data.keyPickupDetails &&
          <Stack gap="0.8rem">

            <Typography variant="text-sm" fontWeight={700} color="#B45309">
              {t('key_pickup_heading')}
            </Typography>

            <Grid>

              <Field label={t('address')} value={data.keyPickupDetails.address} />

              <Field label={t('date_time')} value={keyPickupTime} />

              <Field label={t('contact_name')} value={data.keyPickupDetails.contactName} />

              <Field label={t('contact_phone')} value={data.keyPickupDetails.contactPhone} linkType="tel" />

            </Grid>

          </Stack>
        }

        <ProductInstructionLine
          productCategory={data.productCategory}
          productType={productType}
          productKind={data.productKind}
          productSegment={data.productSegment}
          attributeX={data.creativeInstructionAttributeX}
          attributeY={data.creativeInstructionAttributeY}
          productQuantity={data.productQuantity}
          color="#1F2937"
        />

      </Stack>
    </Box>
  )
}

const Grid: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))', gap: '1.2rem 2.4rem' }}>
    {children}
  </Box>
)

interface FieldProps {
  label: string
  value: string | number | null | undefined
  isMultiline?: boolean
  /** Renders the value as a `tel:` or `mailto:` link so the CT can call or write from the page. */
  linkType?: 'tel' | 'mailto'
  /** Shows a copy button next to the label; called with the trimmed value, resolves to whether it was copied. */
  onCopy?: (text: string) => Promise<boolean>
}

/**
 * Hides itself on null/undefined and on blank or whitespace-only strings: the BE stores free text
 * and contact fields untrimmed and sends '' for blank ones, but treats whitespace as blank itself.
 */
const Field: FC<FieldProps> = ({ label, value, isMultiline, linkType, onCopy }) => {
  const { t } = useTranslation(['common', 'virtual_visit_share'])
  // CopyButton shows "Copied!" before the write settles, so a refused write needs its own message.
  const [isCopyFailed, setCopyFailed] = useState(false)
  const text = typeof value === 'string' ? value.trim() : value
  if (text === null || text === undefined || text === '') return null

  // `overflowWrap: anywhere` keeps a pasted URL or an unbroken token from widening the page on a phone.
  const valueSx = { overflowWrap: 'anywhere', ...(isMultiline ? { whiteSpace: 'pre-wrap' } : {}) }

  const labelText = (
    <Typography variant="text-xs" color="#92400E" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05rem' }}>
      {label}
    </Typography>
  )

  return (
    <Stack gap="0.2rem">

      {onCopy
        ? <Stack direction="row" alignItems="center" justifyContent="space-between" gap="0.8rem">
          {labelText}
          {/* No `copyText`: the caller writes the clipboard (see `copyToClipboard`). The aria-label tells the
              "Copy" buttons apart for a screen reader. */}
          <CopyButton variant="text" showIcon showIdleIcon tooltipPlacement="top" ariaLabel={`${t('copy')}: ${label}`} onCopy={() => onCopy(String(text)).then(isCopied => setCopyFailed(!isCopied))} />
        </Stack>
        : labelText
      }

      {isCopyFailed &&
        <Typography variant="text-xs" color="#B91C1C" role="alert">
          {t('virtual_visit_share:link_copy_error')}
        </Typography>
      }

      {linkType
        ? <Typography variant="text-sm" component="a" href={`${linkType}:${text}`} color="#1F2937" sx={{ ...valueSx, textDecoration: 'underline' }}>{String(text)}</Typography>
        : <Typography variant="text-sm" color="#1F2937" sx={valueSx}>{String(text)}</Typography>
      }

    </Stack>
  )
}
